import {Dispatch, PropsWithChildren, useMemo} from "react";
import {
  createLiveEventReducer,
  initialCreateLiveEventState,
} from "../../reducers/create-live-event";
import {createContext} from "../../../../../context/create-context";
import {
  CreateLiveEventActions,
  CreateLiveEventState,
} from "../../reducers/create-live-event/types";
import {usePricingCurrencies} from "@/features/currencies";
import {useGetProfileContext} from "../../../../../context/profile/profile-context";
import {
  OFFERING_CREATE_DRAFT_KEYS,
  usePersistedReducer,
} from "../../../../../utils/offering-create-draft";

type CreateLiveEventContextState = {
  state: typeof initialCreateLiveEventState;
  dispatch: Dispatch<CreateLiveEventActions>;
  wasDraftRestored: boolean;
};

const [useContext, ContextProvider] = createContext<CreateLiveEventContextState>();

export const useCreateLiveEventContext = useContext;

export const CreateLiveEventProvider = ({children}: PropsWithChildren) => {
  const {state: profileState} = useGetProfileContext();
  const draftScope = useMemo(
    () => ({
      draftKey: OFFERING_CREATE_DRAFT_KEYS.liveEvent,
      email: profileState.profileData.email,
    }),
    [profileState.profileData.email]
  );

  const [state, dispatch, {wasDraftRestored}] = usePersistedReducer(
    createLiveEventReducer,
    draftScope,
    () => initialCreateLiveEventState
  );
  const pricingCurrencies = usePricingCurrencies();

  const newState: CreateLiveEventState = {
    currentCreateLiveEventStep: state.currentCreateLiveEventStep,
    formData: {
      ...state.formData,
      callPriceType: state.formData.callPriceType
        ? state.formData.callPriceType
        : pricingCurrencies.connectedBankStatus.isBankConnected
        ? "paid"
        : "free",
      currencies: state.formData.currencies.length > 0 ? state.formData.currencies : [],
    },
    apiResponseData: state.apiResponseData,
  };

  return (
    <ContextProvider value={{state: newState, dispatch, wasDraftRestored}}>
      {children}
    </ContextProvider>
  );
};
